import { useState } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { cn } from "../../lib/cn";
import {
  ACTIVITY_OPTIONS,
  EXERCISE_TYPE_OPTIONS,
  GOAL_OPTIONS,
  PACE_OPTIONS,
  displayToKg,
  kgToDisplay,
} from "../../onboarding/types";
import {
  saveProgramme,
  showsPace,
  showsTargetWeight,
  type PlanProfile,
  type ProgrammeUpdate,
} from "../../screens/plan-utils";

const SELECT_CLASSES = cn(
  "min-h-[44px] w-full min-w-0 truncate rounded-xl border border-border bg-surface py-3 px-4 text-base",
  "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
);

const PRIMARY_BUTTON = cn(
  "flex min-h-[44px] w-full items-center justify-center rounded-xl bg-accent px-4 py-3 text-sm font-semibold",
  "text-white select-none transition-all duration-200 ease-out active:scale-[0.98]",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export type ProgrammeSheetKind = "goal" | "preferences";

export interface ProgrammeSheetProps {
  readonly kind: ProgrammeSheetKind;
  readonly profile: PlanProfile;
  /** Calls api.engine.updateProgramme (or a demo stub) and resolves with the fresh snapshot. */
  readonly onSave: (patch: ProgrammeUpdate) => Promise<unknown>;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

function SelectField({ label, value, options, onChange, testId }: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly title: string }[];
  readonly onChange: (value: string) => void;
  readonly testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-text">{label}</label>
      <select className={SELECT_CLASSES} value={value} onChange={(event) => onChange(event.target.value)} data-testid={testId}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.title}
          </option>
        ))}
      </select>
    </div>
  );
}

export function ProgrammeSheet({ kind, profile, onSave, onSaved, onClose }: ProgrammeSheetProps) {
  const unit = profile.unitWeight === "lbs" ? "lbs" : "kg";
  const [goalType, setGoalType] = useState(profile.goalType ?? "maintain");
  const [targetWeight, setTargetWeight] = useState(
    profile.targetWeightKg !== undefined ? String(kgToDisplay(profile.targetWeightKg, unit)) : "",
  );
  const [pace, setPace] = useState(profile.weightLossPace ?? "steady");
  const [activityLevel, setActivityLevel] = useState(profile.activityLevel ?? "moderate");
  const [steps, setSteps] = useState(profile.avgDailySteps !== undefined ? String(profile.avgDailySteps) : "");
  const [exerciseType, setExerciseType] = useState(profile.exerciseType ?? "none");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = kind === "goal" ? "Edit goal" : "Edit programme preferences";
  const sheetTestId = kind === "goal" ? "goal-sheet" : "preferences-sheet";

  const handleSave = () => {
    if (saving) return;
    const patch: ProgrammeUpdate =
      kind === "goal"
        ? {
            goalType,
            ...(showsTargetWeight(goalType) && targetWeight.trim() !== ""
              ? { targetWeightKg: displayToKg(Number(targetWeight), unit) }
              : {}),
            ...(showsPace(goalType) ? { weightLossPace: pace } : {}),
          }
        : {
            activityLevel,
            exerciseType,
            ...(steps.trim() !== "" ? { avgDailySteps: Math.max(0, Math.round(Number(steps))) } : {}),
          };
    if (kind === "goal" && showsTargetWeight(goalType) && targetWeight.trim() !== "" && !Number.isFinite(Number(targetWeight))) {
      setError("Enter a valid goal weight.");
      return;
    }
    if (kind === "preferences" && steps.trim() !== "" && !Number.isFinite(Number(steps))) {
      setError("Enter a valid step count.");
      return;
    }
    setSaving(true);
    setError(null);
    void saveProgramme(onSave, patch).then((result) => {
      setSaving(false);
      if (result.type === "saved") {
        onSaved();
      } else {
        setError(result.message);
      }
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col justify-end p-4 pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-h-[85vh] w-full max-w-xl overflow-y-auto">
          <Card data-testid={sheetTestId} className="flex flex-col gap-4 p-5" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text">{title}</h3>
              <button
                type="button"
                aria-label="Close"
                data-testid={`${sheetTestId}-close`}
                className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted select-none"
                onClick={onClose}
              >
                ×
              </button>
            </div>
            {kind === "goal" ? (
              <>
                <SelectField label="Goal" value={goalType} options={GOAL_OPTIONS} onChange={setGoalType} testId="goal-type-select" />
                {showsTargetWeight(goalType) && (
                  <Input
                    data-testid="goal-weight-input"
                    label={`Goal weight (${unit})`}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={targetWeight}
                    onChange={(event) => { setTargetWeight(event.target.value); setError(null); }}
                    placeholder={unit === "lbs" ? "154" : "70"}
                  />
                )}
                {showsPace(goalType) && (
                  <SelectField label="Weekly pace" value={pace} options={PACE_OPTIONS} onChange={setPace} testId="goal-pace-select" />
                )}
              </>
            ) : (
              <>
                <SelectField
                  label="Activity level"
                  value={activityLevel}
                  options={ACTIVITY_OPTIONS}
                  onChange={setActivityLevel}
                  testId="activity-level-select"
                />
                <Input
                  data-testid="steps-input"
                  label="Average daily steps"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={steps}
                  onChange={(event) => { setSteps(event.target.value); setError(null); }}
                  placeholder="7500"
                />
                <SelectField
                  label="Exercise type"
                  value={exerciseType}
                  options={EXERCISE_TYPE_OPTIONS}
                  onChange={setExerciseType}
                  testId="exercise-type-select"
                />
              </>
            )}
            <p className="text-xs leading-relaxed text-text-muted">
              Saving recalculates your plan. Calorie and macro targets are set by the engine — never edited directly.
            </p>
            {error && (
              <p className="text-sm text-danger" role="alert" data-testid={`${sheetTestId}-error`}>
                {error}
              </p>
            )}
            <button type="button" className={PRIMARY_BUTTON} data-testid={`${sheetTestId}-save`} disabled={saving} onClick={handleSave}>
              {saving ? "Saving…" : "Save and recalculate"}
            </button>
          </Card>
        </div>
      </div>
    </>
  );
}
